import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Trash2,
  Plus,
} from "lucide-react";
import api from "./api";
import FinancialProfile from "./FinancialProfile";

const STAGES = [
  "Raw Lead",
  "Active Lead",
  "Discovery Booked",
  "Discovery Completed",
  "Client Closed",
];

export default function ContactDetail() {
  const { contactId } = useParams();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("overview");
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get(`/crm/contacts/${contactId}`).then((r) => setC(r.data));

  useEffect(() => {
    setC(null);
    load();
    // eslint-disable-next-line
  }, [contactId]);

  if (!c) {
    return (
      <div className="text-white/60 text-sm" data-testid="contact-loading">
        Loading contact…
      </div>
    );
  }

  const updateStage = async (stage) => {
    setSaving(true);
    await api.patch(`/crm/contacts/${contactId}`, { stage });
    setSaving(false);
    load();
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    await api.post(`/crm/contacts/${contactId}/notes`, { text: noteText });
    setNoteText("");
    setSaving(false);
    load();
  };

  const delNote = async (id) => {
    await api.delete(`/crm/notes/${id}`);
    load();
  };

  return (
    <div data-testid="contact-detail">
      <Link
        to="/crm/contacts"
        className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6"
      >
        <ArrowLeft size={15} /> Back to contacts
      </Link>

      {/* Header card */}
      <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-6 mb-6 relative overflow-hidden">
        <div
          className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl"
          style={{ background: "rgba(212,165,58,0.15)" }}
        />
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-300/50 to-amber-600/10 flex items-center justify-center text-2xl font-light shrink-0">
            {(c.first_name?.[0] || c.name?.[0] || "?").toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-amber-300/80">
              {c.type} · #{c.contact_id}
            </div>
            <h1 className="text-2xl font-light mt-1">{c.name}</h1>
            <div className="text-sm text-white/50 mt-1">
              {c.demographic || "—"}
            </div>

            <div className="flex flex-wrap gap-4 mt-4 text-sm">
              {c.email && (
                <a
                  href={`mailto:${c.email}`}
                  className="flex items-center gap-2 text-white/80 hover:text-amber-200"
                >
                  <Mail size={14} /> {c.email}
                </a>
              )}
              {c.phone && (
                <a
                  href={`tel:${c.phone}`}
                  className="flex items-center gap-2 text-white/80 hover:text-amber-200"
                >
                  <Phone size={14} /> {c.phone}
                </a>
              )}
              {c.province && (
                <span className="flex items-center gap-2 text-white/60">
                  <MapPin size={14} /> {c.province}
                </span>
              )}
            </div>
          </div>

          {/* Stage + cadence selector */}
          <div className="text-right space-y-3">
            <div>
              <div className="text-[11px] text-white/50 uppercase tracking-widest mb-2">
                Stage
              </div>
              <select
                value={c.stage || "Raw Lead"}
                onChange={(e) => updateStage(e.target.value)}
                disabled={saving}
                data-testid="stage-select"
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-300/40"
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[11px] text-white/50 uppercase tracking-widest mb-2">
                Follow-up cadence
              </div>
              <select
                value={c.followup_cadence || ""}
                onChange={async (e) => {
                  await api.patch(`/crm/contacts/${contactId}`, { followup_cadence: e.target.value || null });
                  load();
                }}
                data-testid="cadence-select"
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-300/40"
              >
                <option value="">None</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="semiannual">Semi-annual</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/[0.06]">
        {["overview", "profile", "cases", "notes"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={`px-4 py-2.5 text-sm capitalize transition ${
              tab === t
                ? "text-amber-200 border-b-2 border-amber-300"
                : "text-white/50 hover:text-white/80"
            }`}
          >
            {t}
            {t === "cases" && c.cases?.length ? ` (${c.cases.length})` : ""}
            {t === "notes" && c.notes?.length ? ` (${c.notes.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview c={c} />}
      {tab === "profile" && (
        <FinancialProfile
          profile={c.profile}
          contactId={contactId}
          onSaved={load}
        />
      )}
      {tab === "cases" && <Cases c={c} />}
      {tab === "notes" && (
        <Notes
          c={c}
          noteText={noteText}
          setNoteText={setNoteText}
          addNote={addNote}
          delNote={delNote}
          saving={saving}
        />
      )}
    </div>
  );
}

function Overview({ c }) {
  const rows = [
    ["Lead Source", c.lead_source],
    ["Assigned Advisor", c.agent],
    ["Policy Advisor", c.policy_agent],
    ["Annual Premium", c.deal_size ? `$${Math.round(c.deal_size)}` : "—"],
    ["Date Arrived", c.date_arrived?.slice(0, 10)],
    ["Job Title", c.job_title],
    ["Employer", c.employer],
    ["Marital Status", c.marital_status],
    ["Residency", c.residency_status],
    ["Will Status", c.will_status],
    ["Last Review", c.last_annual_review?.slice?.(0, 10)],
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <h3 className="text-sm text-white/70 uppercase tracking-widest mb-4">
          Details
        </h3>
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          {rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-white/50">{k}</dt>
              <dd className="text-white/90 truncate">{v || "—"}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
      {c.google_folder && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
          <h3 className="text-sm text-white/70 uppercase tracking-widest mb-4">
            Resources
          </h3>
          <div className="text-sm text-white/70">{c.google_folder}</div>
        </div>
      )}
    </div>
  );
}

function Cases({ c }) {
  if (!c.cases?.length) {
    return (
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 text-center text-white/50">
        <Briefcase size={20} className="mx-auto mb-3 opacity-40" />
        No cases on file.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
            <th className="px-4 py-3 font-normal">Case Date</th>
            <th className="px-4 py-3 font-normal">Company</th>
            <th className="px-4 py-3 font-normal">Product</th>
            <th className="px-4 py-3 font-normal text-right">Premium</th>
            <th className="px-4 py-3 font-normal">Policy #</th>
            <th className="px-4 py-3 font-normal">Status</th>
          </tr>
        </thead>
        <tbody>
          {c.cases.map((cs) => (
            <tr
              key={cs.id}
              className="border-b border-white/[0.04]"
              data-testid={`case-row-${cs.case_id}`}
            >
              <td className="px-4 py-3">{cs.case_date?.slice(0, 10)}</td>
              <td className="px-4 py-3">{cs.company}</td>
              <td className="px-4 py-3">{cs.product}</td>
              <td className="px-4 py-3 text-right tabular-nums">
                ${Math.round(cs.annual_premium || 0)}
              </td>
              <td className="px-4 py-3 text-white/70">{cs.policy_number || "—"}</td>
              <td className="px-4 py-3 text-white/70">{cs.status || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Notes({ c, noteText, setNoteText, addNote, delNote, saving }) {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Add a note about this contact…"
          rows={3}
          data-testid="note-input"
          className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-300/40 resize-none"
        />
        <div className="flex justify-end mt-3">
          <button
            onClick={addNote}
            disabled={saving || !noteText.trim()}
            data-testid="note-save"
            className="px-4 py-2 rounded-lg text-sm font-medium text-black disabled:opacity-40"
            style={{
              background:
                "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)",
            }}
          >
            <span className="flex items-center gap-2">
              <Plus size={14} /> Add note
            </span>
          </button>
        </div>
      </div>
      {c.notes?.length ? (
        <div className="space-y-3">
          {c.notes.map((n) => (
            <div
              key={n.id}
              className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4"
              data-testid={`note-${n.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <p className="text-sm whitespace-pre-wrap text-white/90">
                  {n.text}
                </p>
                <button
                  onClick={() => delNote(n.id)}
                  className="text-white/40 hover:text-red-300"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="text-xs text-white/40 mt-2">
                {n.author} · {n.created_at?.slice(0, 16).replace("T", " ")}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 text-center text-white/50">
          No notes yet.
        </div>
      )}
    </div>
  );
}
